import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useVoice = () => {
  const { token } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  // Generate and play speech
  const speak = useCallback(async (text, character = 'tank', priority = 'normal') => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/voice/generate`,
        { text, character, priority },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.audio_url) {
        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }

        // Create and play new audio
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;

        audio.onplay = () => setIsPlaying(true);
        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };
        audio.onerror = (e) => {
          console.error('Audio playback error:', e);
          setError('Audio playback failed');
          setIsPlaying(false);
          audioRef.current = null;
        };

        await audio.play();
      }
    } catch (e) {
      console.error('Voice generation error:', e);
      setError(e.response?.data?.detail || 'Voice generation failed');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Generate alert with appropriate character
  const speakAlert = useCallback(async (message, severity = 'medium') => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/voice/alert`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { message, severity }
        }
      );

      if (response.data.audio_url) {
        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }

        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;

        audio.onplay = () => setIsPlaying(true);
        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setError('Audio playback failed');
          setIsPlaying(false);
        };

        await audio.play();
      }
    } catch (e) {
      console.error('Alert voice error:', e);
      setError(e.response?.data?.detail || 'Alert generation failed');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Stop playback
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
  }, []);

  // Play Tank introduction
  const playTankIntro = useCallback(() => {
    speak(
      "Hey there, this is Tank. I've got your six. Anything happens in your area, I'll let you know. Stay frosty out there.",
      'tank',
      'normal'
    );
  }, [speak]);

  // Play Daisy introduction
  const playDaisyIntro = useCallback(() => {
    speak(
      "This is Daisy at Homebase. I'll be handling critical intel and emergency briefings. When I call, it's important. Stay safe out there.",
      'daisy',
      'high'
    );
  }, [speak]);

  return {
    speak,
    speakAlert,
    stop,
    playTankIntro,
    playDaisyIntro,
    isPlaying,
    isLoading,
    error
  };
};

export default useVoice;
